import {addDays} from 'date-fns/addDays'
import {addHours} from 'date-fns/addHours'
import {pipe} from 'ramda'

import {
  cookieSecuritySettings,
  getCookie,
  setCookie
} from '@vio/core/src/_web/utils/cookies'
import {
  safelyGetStorageItem,
  safelyRemoveStorageItem,
  safelySetStorageItem
} from '@vio/core/src/_web/utils/persistence'
import {
  removeOutdatedUserSearchHistory,
  selectUniqueSearches,
  updateExpiredDates,
  UserSearchHistoryItem
} from '@vio/core/src/searchHistory'
import {getDomainName} from '@vio/core/src/utils/url'

export const getPopupOnExitShown = (): boolean =>
  Boolean(getCookie('isPopupOnExitShown'))
export const setPopupOnExitShown = (): void => {
  setCookie('isPopupOnExitShown', '1', {
    expires: addHours(new Date(), 2),
    domain: getDomainName(window.location.hostname),
    ...cookieSecuritySettings(true)
  })
}

// Top Banner persistence functions
export const getTopBannerDismissed = (): boolean =>
  Boolean(getCookie('isTopBannerDismissed'))

export const setTopBannerDismissed = (): void => {
  // Set cookie to expire in 30 days
  setCookie('isTopBannerDismissed', '1', {
    expires: addDays(new Date(), 30),
    domain: getDomainName(window.location.hostname),
    ...cookieSecuritySettings(true)
  })
}

export const getSurveyBannerDismissed = (): boolean => {
  try {
    return Boolean(getCookie('isSurveyBannerDismissed'))
  } catch {
    return true
  }
}

export const setSurveyBannerDismissed = (): void => {
  try {
    // Hide the survey banner for 30 days after the user answers or closes it
    setCookie('isSurveyBannerDismissed', '1', {
      expires: addDays(new Date(), 30),
      domain: getDomainName(window.location.hostname),
      ...cookieSecuritySettings(true)
    })
  } catch {
    // A failed write must not break the close or submit interaction. The
    // banner is already hidden for this page; the next visit re-reads the cookie.
  }
}

// The chosen statement lives in localStorage so it survives refreshes, tab and
// browser closes, and is cleared once the user answers or closes the banner.
export const getSurveyStatementKey = (): string | null | undefined =>
  safelyGetStorageItem('localStorage', 'trustSurveyStatementKey')

export const setSurveyStatementKey = (key: string): void =>
  safelySetStorageItem('localStorage', 'trustSurveyStatementKey', key)

export const removeSurveyStatementKey = (): void =>
  safelyRemoveStorageItem('localStorage', 'trustSurveyStatementKey')

export const selectUniqueNonExpiredHotelSearches = pipe(
  JSON.parse,
  selectUniqueSearches,
  updateExpiredDates
)

export const getUniqueRecentHotelSearches = (): UserSearchHistoryItem[] => {
  const empty = []
  const searchHistory = safelyGetStorageItem(
    'localStorage',
    'searchHistory',
    'getUserSearchHistory failed'
  )

  try {
    return selectUniqueNonExpiredHotelSearches(searchHistory)
  } catch (error) {
    return empty
  }
}

const SEARCH_HISTORY_LIMIT = 4

export const getUserSearchHistory = (
  limitToSearchHistory = SEARCH_HISTORY_LIMIT,
  removeOutdated = true
): UserSearchHistoryItem[] => {
  const empty = []
  const searchHistory = safelyGetStorageItem(
    'localStorage',
    'searchHistory',
    'getUserSearchHistory failed'
  )

  try {
    const parsedSearchHistory = JSON.parse(searchHistory).slice(
      0,
      limitToSearchHistory
    )

    if (removeOutdated) {
      return removeOutdatedUserSearchHistory(parsedSearchHistory)
    }

    return parsedSearchHistory
  } catch (error) {
    return empty
  }
}

export const setUserSearchHistory = (
  searchData: UserSearchHistoryItem[]
): void =>
  safelySetStorageItem(
    'localStorage',
    'searchHistory',
    JSON.stringify(searchData),
    'setUserSearchHistory failed'
  )

export const removeUserSearchHistoryById = (historyId: string) => {
  const oldList = getUserSearchHistory()
  const newList = oldList.filter(item => item.historyId !== historyId)
  setUserSearchHistory(newList)
}

export const isCookiesAcceptedByUser = (): boolean =>
  Boolean(
    safelyGetStorageItem(
      'localStorage',
      'cookiePreference',
      'isCookiesAcceptedByUser failed'
    )
  )

export const setCookiesAcceptedByUser = (): void => {
  safelySetStorageItem(
    'localStorage',
    'cookiePreference',
    'acceptedByUser',
    'setCookiesAcceptedByUser failed'
  )
}

export const getLogEvents = (): string =>
  safelyGetStorageItem('localStorage', 'logEvents', 'getLogEvents failed')

export const setLogEvents = (logEvents: string): void =>
  safelySetStorageItem(
    'localStorage',
    'logEvents',
    logEvents,
    'setLogEvents failed'
  )

// Price Insights tooltip persistence — show once per lifetime
const PRICE_INSIGHTS_TOOLTIP_KEY = 'priceInsightsTooltipDismissed'

export const getPriceInsightsTooltipDismissed = (): boolean =>
  Boolean(
    safelyGetStorageItem(
      'localStorage',
      PRICE_INSIGHTS_TOOLTIP_KEY,
      'getPriceInsightsTooltipDismissed failed'
    )
  )

export const setPriceInsightsTooltipDismissed = (): void =>
  safelySetStorageItem(
    'localStorage',
    PRICE_INSIGHTS_TOOLTIP_KEY,
    '1',
    'setPriceInsightsTooltipDismissed failed'
  )

// AP proactive price-alert prompt — show at most once per day per device.
// A localStorage timestamp (not a cookie) so the cap survives reloads within the
// 24h window. `now` is injectable for tests.
const PRICE_ALERT_PROMPT_SHOWN_KEY = 'apPriceAlertPromptShownAt'
const PRICE_ALERT_PROMPT_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

const readStoredTimestamp = (key: string): number | undefined => {
  const value = Number(
    safelyGetStorageItem('localStorage', key, `${key} read failed`)
  )

  return value && Number.isFinite(value) ? value : undefined
}

const wasShownWithinTtl = (
  key: string,
  ttlMs: number,
  now: number
): boolean => {
  const shownAt = readStoredTimestamp(key)

  if (shownAt === undefined) return false

  const elapsed = now - shownAt

  return elapsed >= 0 && elapsed < ttlMs
}

export const wasPriceAlertPromptShownToday = (
  now: number = Date.now()
): boolean =>
  wasShownWithinTtl(
    PRICE_ALERT_PROMPT_SHOWN_KEY,
    PRICE_ALERT_PROMPT_TTL_MS,
    now
  )

export const setPriceAlertPromptShown = (now: number = Date.now()): void =>
  safelySetStorageItem(
    'localStorage',
    PRICE_ALERT_PROMPT_SHOWN_KEY,
    String(now),
    'setPriceAlertPromptShown failed'
  )

// SRP Deal Freeze education banner persistence
const DF_EDUCATION_BANNER_SHOWN_KEY = 'dealFreezeEducationBannerShownAt'
const DF_EDUCATION_BANNER_CTA_CLICKED_KEY =
  'dealFreezeEducationBannerCtaClicked'
const DEAL_FREEZE_EDUCATION_BANNER_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

export const wasDealFreezeEducationBannerShownToday = (
  now: number = Date.now()
): boolean =>
  wasShownWithinTtl(
    DF_EDUCATION_BANNER_SHOWN_KEY,
    DEAL_FREEZE_EDUCATION_BANNER_TTL_MS,
    now
  )

export const hasDealFreezeEducationBannerEverShown = (): boolean =>
  readStoredTimestamp(DF_EDUCATION_BANNER_SHOWN_KEY) !== undefined

export const setDealFreezeEducationBannerShown = (
  now: number = Date.now()
): void =>
  safelySetStorageItem(
    'localStorage',
    DF_EDUCATION_BANNER_SHOWN_KEY,
    String(now),
    'setDealFreezeEducationBannerShown failed'
  )

export const wasDealFreezeEducationBannerCtaClicked = (): boolean =>
  Boolean(
    safelyGetStorageItem(
      'localStorage',
      DF_EDUCATION_BANNER_CTA_CLICKED_KEY,
      'wasDealFreezeEducationBannerCtaClicked failed'
    )
  )

export const setDealFreezeEducationBannerCtaClicked = (): void =>
  safelySetStorageItem(
    'localStorage',
    DF_EDUCATION_BANNER_CTA_CLICKED_KEY,
    '1',
    'setDealFreezeEducationBannerCtaClicked failed'
  )
